import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface AnalyticsData {
  userGrowth: number[];
  postActivity: number[];
  engagementRate: number[];
  reportDistribution: {
    spam: number;
    harassment: number;
    hate_speech: number;
    inappropriate_content: number;
    violence: number;
    copyright: number;
    other: number;
  };
}

export function Analytics() {
  const [data, setData] = useState<AnalyticsData>({
    userGrowth: [],
    postActivity: [],
    engagementRate: [],
    reportDistribution: {
      spam: 0,
      harassment: 0,
      hate_speech: 0,
      inappropriate_content: 0,
      violence: 0,
      copyright: 0,
      other: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      // Get user growth (last 7 days)
      const { data: users, error: usersError } = await supabase
        .from('profiles')
        .select('created_at')
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());

      if (usersError) throw usersError;

      // Get post activity (last 7 days)
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('created_at')
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());

      if (postsError) throw postsError;

      // Get report distribution
      const { data: reports, error: reportsError } = await supabase
        .from('reports')
        .select('type');

      if (reportsError) throw reportsError;

      // Process data
      const userGrowth = processTimeSeriesData(users);
      const postActivity = processTimeSeriesData(posts);
      const engagementRate = calculateEngagementRate(userGrowth, postActivity);
      const reportDistribution = processReportDistribution(reports);

      setData({
        userGrowth,
        postActivity,
        engagementRate,
        reportDistribution
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const processTimeSeriesData = (data: any[]) => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    return last7Days.map(date => {
      return data.filter(item => 
        item.created_at.split('T')[0] === date
      ).length;
    });
  };

  const calculateEngagementRate = (users: number[], posts: number[]) => {
    return users.map((userCount, index) => {
      if (userCount === 0) return 0;
      return (posts[index] / userCount) * 100;
    });
  };

  const processReportDistribution = (reports: any[]) => {
    const distribution = {
      spam: 0,
      harassment: 0,
      hate_speech: 0,
      inappropriate_content: 0,
      violence: 0,
      copyright: 0,
      other: 0
    };

    reports.forEach(report => {
      distribution[report.type as keyof typeof distribution]++;
    });

    return distribution;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-royal-purple"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        {error}
      </div>
    );
  }

  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  });

  return (
    <div className="space-y-6">
      {/* User Growth */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">User Growth</h3>
        <Line
          data={{
            labels: last7Days,
            datasets: [{
              label: 'New Users',
              data: data.userGrowth,
              borderColor: '#7851A9',
              backgroundColor: 'rgba(120, 81, 169, 0.1)',
              fill: true,
              tension: 0.4
            }]
          }}
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: false
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  stepSize: 1
                }
              }
            }
          }}
        />
      </div>

      {/* Post Activity */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Post Activity</h3>
        <Bar
          data={{
            labels: last7Days,
            datasets: [{
              label: 'Posts',
              data: data.postActivity,
              backgroundColor: 'rgba(120, 81, 169, 0.8)'
            }]
          }}
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: false
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  stepSize: 1
                }
              }
            }
          }}
        />
      </div>

      {/* Engagement Rate */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Engagement Rate</h3>
        <Line
          data={{
            labels: last7Days,
            datasets: [{
              label: 'Engagement Rate (%)',
              data: data.engagementRate,
              borderColor: '#4169E1',
              backgroundColor: 'rgba(65, 105, 225, 0.1)',
              fill: true,
              tension: 0.4
            }]
          }}
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: false
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: (value) => `${value}%`
                }
              }
            }
          }}
        />
      </div>

      {/* Report Distribution */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold mb-4">Report Distribution</h3>
        <div className="h-[300px]">
          <Doughnut
            data={{
              labels: [
                'Spam',
                'Harassment',
                'Hate Speech',
                'Inappropriate Content',
                'Violence',
                'Copyright',
                'Other'
              ],
              datasets: [{
                data: Object.values(data.reportDistribution),
                backgroundColor: [
                  '#FF6384',
                  '#36A2EB',
                  '#FFCE56',
                  '#4BC0C0',
                  '#9966FF',
                  '#FF9F40',
                  '#C9CBCF'
                ]
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'right'
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}